import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { FileSaverService } from './file-saver.service';
import {AttributeLocaleFields, Locale, LocaleMappings, LocaleUpdateFields} from '@emm/core/models/locale.model';
import { ConsumerService } from '@emm/core/services/consumer.service';
import { LocalesService } from '@emm/core/services/locales.service';
import {LocationSetService} from '@emm/core/services/location-set.service';
import {LocationStatusService} from '@emm/core/services/location-status.service';
import {ReportService} from '@emm/core/services/report.service';
import {VenueService} from '@emm/core/services/venue.service';
import {LocationSet} from '@emm/core/models/location-set.model';
import {Consumer} from '@emm/core/models/consumer.model';
import {LocationStatus} from '@emm/core/models/location-status.model';
import {Venue} from '@emm/core/models/venue.model';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {ItemSummary} from '@emm/core/models/item-summary.model';
import {Screen} from '@emm/core/models/screen.model';


@Injectable({ providedIn: 'root' })
export class SharedApiService { // FIXME reconsider purpose of this class since many unused methods

    constructor(
        private consumerService: ConsumerService,
        private fileSaverService: FileSaverService,
        private localesService: LocalesService,
        private locationSetService: LocationSetService,
        private locationStatusService: LocationStatusService,
        private reportService: ReportService,
        private venueService: VenueService,
    ) {}

    getAllLocationSetsByTag(tag: string): Observable<LocationSet[]> {
        return this.locationSetService.getAllLocationSetsByTag(tag);
    }

    getScreens(): Observable<Screen[]> {
        return of([
            { name: 'Nutrition', selected: true, route: 'nutrition' },
            { name: 'Global Menus', selected: false, route: 'global-menus' },
            { name: 'Location Specific Menus', selected: false, route: 'location-specific-menus' },
            { name: 'Emergency Kitchen', selected: false, route: 'emergency-kitchen' },
            { name: 'Location Status', selected: false, route: 'location-status' },
            { name: 'Local Menu Edits', selected: false, route: 'local-menu-edits' },
            { name: 'Master Menu System', selected: false, route: 'master-menu-system' }
        ]);
    }

    getLocaleOptions(): Observable<Locale[]> {
        return this.localesService.getLocaleOptions();
    }

    getLocaleMappings(): Observable<LocaleMappings> {
        return this.localesService.getLocaleMappings();
    }

    downloadNutrition(countryCode: string): Observable<any> {
        return this.reportService.getNutritionData(countryCode);
    }

    downloadNutritionProfiles(effectiveDate: string): Observable<any> {
        return this.reportService.getNutritionProfilesData(effectiveDate);
    }

    generateDownloadFile(res: any, heading: string) {
        return this.fileSaverService.generateDownloadFile(res, heading);
    }

    generateDownloadFileAsCsv(data: any, heading, fields?: any) {
        return this.fileSaverService.generateDownloadFileAsCsv(data, heading, fields);
    }

    downloadFileAsCsv(data: any, heading) {
        return this.fileSaverService.downloadFileAsCsv(data, heading);
    }

    getAllConsumers(): Observable<Consumer[]> {
        return this.consumerService.getAllConsumers();
    }

    getAllLocationStatus(): Observable<LocationStatus[]> {
        return this.locationStatusService.getEmergencyStatuses();
    }

    getOtherLocationStatus(): Observable<LocationStatus[]> {
        return this.locationStatusService.getOtherStatuses();
    }

    downloadLocationStatus(statuses): Observable<any> {
        return this.locationStatusService.downloadLocationStatus(statuses.selected);
    }

    downloadLocalMenuEdits(): Observable<any> {
        return this.reportService.getLocalMenuEdits();
    }

    downloadMasterMenuSystem(effectiveDate: string): Observable<any> {
        return this.reportService.getMasterMenuSystem(effectiveDate);
    }

    downloadGlobalMenu(
        consumerTag: string,
        locationTag: string,
        effectiveDate: string
    ): Observable<any> {
        return this.reportService.getGlobalMenu(consumerTag, locationTag, effectiveDate);
    }

    downloadLocationSpecificMenu(
        consumerTag: string,
        locationTag: string,
        effectiveDate: string
    ): Observable<any> {
        return this.reportService.getLocationSpecificMenu(consumerTag, locationTag, effectiveDate);
    }

    getAttributeLocaleFields(
        itemTag: string,
        attributeTag: string
    ): Observable<AttributeLocaleFields> {
        return this.localesService.getAttributeLocaleFields(itemTag, attributeTag);
    }

    saveAttributeLocaleFields(itemTag: string, updatedFields: LocaleUpdateFields[]): Observable<null> {
        return this.localesService.saveAttributeLocaleFields(itemTag, updatedFields);
    }

    getVenuesByLocation(location: string): Observable<Venue[]> {
        return this.venueService.getVenuesByLocation(location);
    }

    getConsumersByVenue(venueId: string | number): Observable<Consumer[]> {
        return this.venueService.getConsumersByVenue(venueId);
    }

    getVenueMenu(location: string, venueNumber: string | number, effectiveDate?: string): Observable<Venue> {
        return this.venueService.getVenueMenu(location, venueNumber, effectiveDate);
    }

    getMenuItemForVenue(consumer: string, location: string, venueNumber: string | number, path?: string,
                        effectiveDate?: string): Observable<ItemDetail> {
        return this.venueService.getMenuItemForVenue(consumer, location, venueNumber, path, effectiveDate);
    }

    // tslint:disable-next-line:max-line-length
    getMenuItemChildrenForVenue(consumer: string, location: string, venueNumber: string | number, path?: string,
                                effectiveDate?: string): Observable<ItemSummary[]> {
        return this.venueService.getMenuItemChildrenForVenue(consumer, location, venueNumber, path, effectiveDate);
    }
}
